import type { TripSectionId } from "./TripsPage";
import type { GuidedStepKind } from "./guidedPlan";

/** Explicit user choices: AI may suggest steps but never silently hide a section. */
export type TripPurpose = "leisure" | "family" | "work" | "other";
export type TripAccommodation = "booking" | "friends" | "mixed" | "unsure";
export type TripTransport = "flight" | "train" | "car" | "other" | "unsure";
export interface TripCompanion {
  name: string;
  identifier: string;
}
export interface TripSetupAnswers {
  travelCompany?: "solo" | "company";
  companions?: TripCompanion[];
  accommodations?: TripAccommodation[];
  transports?: TripTransport[];
  purpose: TripPurpose;
  accommodation: TripAccommodation;
  transport: TripTransport;
  notes: string;
}
export interface TripSetup {
  answers: TripSetupAnswers;
  hiddenSections: TripSectionId[];
  updatedAt: string;
}
export const defaultTripSetupAnswers: TripSetupAnswers = {
  purpose: "leisure", accommodation: "unsure", transport: "unsure", notes: "", travelCompany: "solo", companions: [], accommodations: ["unsure"], transports: ["unsure"]
};

export function proposeTripSections(answers: TripSetupAnswers): { hiddenSections: TripSectionId[]; steps: GuidedStepKind[] } {
  // The hotel section is optional for staying with family/friends. The traveller approves this explicitly.
  const accommodations = answers.accommodations?.length ? answers.accommodations : [answers.accommodation];
  const transports = answers.transports?.length ? answers.transports : [answers.transport];
  const hiddenSections: TripSectionId[] = accommodations.length === 1 && accommodations[0] === "friends" ? ["hotels"] : [];
  const steps: GuidedStepKind[] = [];
  if (hiddenSections.length === 0) steps.push("accommodation");
  if (transports.includes("flight")) steps.push("flight", "transfer");
  if (transports.includes("train")) steps.push("train");
  if (transports.includes("other") || transports.includes("unsure")) steps.push("transfer");
  steps.push("documents");
  if (answers.purpose !== "work") steps.push("activity");
  return { hiddenSections, steps };
}

export function parseAiGuidedSuggestions(output: string): GuidedStepKind[] {
  // Treat generative text as untrusted: accept only a constrained, validated JSON list.
  const match = output.match(/\{[\s\S]*\}/);
  if (!match || match[0].length > 4000) return [];
  try {
    const parsed: unknown = JSON.parse(match[0]);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
    const kinds = Reflect.get(parsed, "suggestedStepKinds");
    if (!Array.isArray(kinds)) return [];
    const permitted: GuidedStepKind[] = ["accommodation", "flight", "train", "transfer", "documents", "activity", "insurance", "other"];
    return [...new Set(kinds.filter((value): value is GuidedStepKind => typeof value === "string" && permitted.includes(value as GuidedStepKind)))].slice(0, 8);
  } catch { return []; }
}

export function normalizeTripSetup(value: unknown): TripSetup | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const o = value as Partial<TripSetup>;
  const a = o.answers;
  if (!a || typeof a !== "object") return null;
  const purposes: TripPurpose[] = ["leisure", "family", "work", "other"];
  const accommodation: TripAccommodation[] = ["booking", "friends", "mixed", "unsure"];
  const transport: TripTransport[] = ["flight", "train", "car", "other", "unsure"];
  if (!purposes.includes(a.purpose) || !accommodation.includes(a.accommodation) || !transport.includes(a.transport)) return null;
  const validAccommodations = Array.isArray(a.accommodations) ? a.accommodations.filter((v): v is TripAccommodation => accommodation.includes(v)) : [];
  const validTransports = Array.isArray(a.transports) ? a.transports.filter((v): v is TripTransport => transport.includes(v)) : [];
  const rawCompanions = Array.isArray(a.companions) ? a.companions : [];
  const companions: TripCompanion[] = rawCompanions.filter((item): item is TripCompanion => !!item && typeof item.name === "string" && typeof item.identifier === "string")
    .slice(0, 20).map(item => ({name:item.name.trim().slice(0,100), identifier:item.identifier.trim().slice(0,150)}));
  return { answers: { purpose: a.purpose, accommodation: a.accommodation, transport: a.transport,
    notes: typeof a.notes === "string" ? a.notes.slice(0, 500) : "",
    travelCompany: a.travelCompany === "company" ? "company" : "solo",
    companions,
    accommodations: validAccommodations.length ? validAccommodations : [a.accommodation],
    transports: validTransports.length ? validTransports : [a.transport] },
    hiddenSections: Array.isArray(o.hiddenSections) && o.hiddenSections.includes("hotels") ? ["hotels"] : [],
    updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : ""
  };
}
